import React from "react";
import { useAuth } from "../../context/AuthContext";
import { GlobalNav } from "../shared/GlobalNav";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { HelpCircle, Shield, FileText, Users } from "lucide-react";

const HelpPage = () => {
  const { user, logout } = useAuth();
  
  const faqs = [
    {
      question: "How do I create and send a project to my client?",
      answer: "Click 'Create Project' on your dashboard, fill in the project details and client information, then click 'Create & Send to Client' to immediately email them a payment link. Alternatively, click 'Create Project' to save it and send later."
    },
    {
      question: "What payment structures can my clients choose from?",
      answer: "Clients can choose from 5 options: 100% Upfront, 50/50 Split (deposit + balance), 3 Monthly Instalments, Credit Card, or Buy Now Pay Later (BNPL — coming soon). You set the project value, and they choose how to pay."
    },
    {
      question: "How does the contract signing process work?",
      answer: "After your client selects a payment structure, they review and sign a smart contract. They enter their details, confirm their identity, and receive a verification email. Once verified, they can proceed to payment."
    },
    {
      question: "What's the difference between Hosted Link and Embedded Checkout?",
      answer: "Hosted Link gives you a secure Paify URL to share with clients. Embedded Checkout (Embedded Plan) lets you add the checkout directly to your website with your full branding using an iframe code."
    },
    {
      question: "How do I set up my email to send from my business address?",
      answer: "Go to Settings and configure your SMTP settings under 'Connect Your Email Account'. You can use Gmail, Outlook, or any custom SMTP server. All payment links and reminders will be sent from your business email."
    },
    {
      question: "What is the VAT Vault?",
      answer: "The VAT Vault (Add-on feature) automatically separates your VAT from each payment into a dedicated pot. It tracks your VAT liability and provides monthly summaries, so you're never caught short at tax time."
    },
    {
      question: "How do payment reminders work?",
      answer: "With Paify Plus, automated reminders are sent on Day 1, Day 7, then every 10 days (Day 17, 27, 37, 47, 57) after a milestone's due date. You can toggle individual stages on/off in Settings → Communications → Reminders & Dunning. On Day 61, Late Payment Protection takes over automatically: statutory interest (8% + Bank of England base rate) and a £40 recovery fee are applied to the invoice under the Late Payment of Commercial Debts (Interest) Act 1998."
    },
    {
      question: "Will my client get asked to leave a Google review?",
      answer: "Yes — if you've enabled 'Google Review Requests' in Settings → Communications and added your Google review link. After you mark a project complete, Paify sends a 'Project Completion' email to the client immediately using your configured completion message, then waits the number of days you set in 'Review Request Delay' and sends a polite Google review request email using your 'Review Request Message' + Google review link."
    },
    {
      question: "What happens after a client makes a payment?",
      answer: "Both you and your client receive confirmation emails. The client gets a receipt with invoice and contract PDFs attached. You can track all payments in your dashboard and the project status updates automatically."
    },
    {
      question: "Can I create invoices without the full checkout flow?",
      answer: "Yes! Toggle 'Invoice Project' when creating a project to create a simple invoice. You can edit invoice details using the blue pencil icon in your project list."
    },
    {
      question: "How do I connect my Stripe account?",
      answer: "Go to the Providers page and click 'Connect' under Stripe. You'll be redirected to Stripe to authorise the connection. Once connected, all payments go directly to your Stripe account."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="help" />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Help & Support</h1>
          <p className="text-gray-600 font-body">Get help with Paify and find answers to common questions.</p>
        </div>

        <div className="space-y-8">
          {/* Contact Support */}
          <Card className="border-0 shadow-sm bg-gradient-to-r from-black to-gray-800 text-white">
            <CardContent className="p-6 text-center">
              <h2 className="text-xl font-semibold mb-2 font-heading">Need help?</h2>
              <p className="text-gray-300 mb-4 font-body">Our support team is here to help you get the most out of Paify.</p>
              <Button 
                onClick={() => window.open('mailto:support@paify.io', '_blank')}
                className="bg-white text-black hover:bg-gray-100 font-body"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
            </CardContent>
          </Card>

          {/* FAQ */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                    <h3 className="font-semibold text-gray-900 mb-2 font-heading">{faq.question}</h3>
                    <p className="text-gray-600 font-body">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 font-heading">
                <span>Quick Links</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button variant="outline" className="justify-start font-body" asChild>
                  <a href="/privacy-policy">
                    <Shield className="w-4 h-4 mr-2" />
                    Privacy Policy
                  </a>
                </Button>
                <Button variant="outline" className="justify-start font-body" asChild>
                  <a href="/terms-of-service">
                    <FileText className="w-4 h-4 mr-2" />
                    Terms of Service
                  </a>
                </Button>
                <Button variant="outline" className="justify-start font-body opacity-50" disabled>
                  <Users className="w-4 h-4 mr-2" />
                  Community Forum
                </Button>
                <Button variant="outline" className="justify-start font-body opacity-50" disabled>
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Video Tutorials
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default HelpPage;
